import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '@/lib/api';
import { fmtInt, fmtMs, fmtUsd, fmtWhen } from '@/lib/format';
import { rk, useAgents } from '@/lib/hooks';
import type { TraceRow } from '@/lib/types';
import { Badge, Button, Card, Empty, ErrorNote, Input, Loading, Select } from './ui';

export type TraceFilters = { agentId?: string; status?: string; source?: string; subjectKey?: string; subjectValue?: string; project?: string };

const qs = (f: TraceFilters, cursor?: string | null) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) p.set(k, v);
  if (cursor) p.set('cursor', cursor);
  p.set('limit', '50');
  return p.toString();
};

export const TraceList = ({ fixed = {}, showAgent = true }: { fixed?: TraceFilters; showAgent?: boolean }) => {
  const agents = useAgents();
  const navigate = useNavigate();
  const [f, setF] = useState<TraceFilters>({ source: 'runtime' });
  const [subject, setSubject] = useState({ key: 'applicationId', value: '' });
  const filters = { ...f, ...fixed, ...(subject.value ? { subjectKey: subject.key, subjectValue: subject.value } : {}) };
  const q = useInfiniteQuery({
    queryKey: rk('traces', filters),
    queryFn: ({ pageParam }) => api<{ traces: TraceRow[]; nextCursor: string | null }>(`/v1/traces?${qs(filters, pageParam)}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 15_000,
  });
  const rows = q.data?.pages.flatMap((p) => p.traces) ?? [];

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
        {showAgent && !fixed.agentId && (
          <Select className="w-56" value={f.agentId ?? ''} onChange={(e) => setF({ ...f, agentId: e.target.value || undefined })}>
            <option value="">All agents</option>
            {(agents.data?.agents ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.slug}
              </option>
            ))}
          </Select>
        )}
        <Select className="w-32" value={f.status ?? ''} onChange={(e) => setF({ ...f, status: e.target.value || undefined })}>
          <option value="">Any status</option>
          <option value="ok">OK</option>
          <option value="error">Errors</option>
        </Select>
        <Select className="w-36" value={f.source ?? ''} onChange={(e) => setF({ ...f, source: e.target.value || undefined })}>
          <option value="runtime">Production</option>
          <option value="playground">Playground</option>
          <option value="">Both</option>
        </Select>
        <div className="flex items-center gap-1">
          <Select className="w-36" value={subject.key} onChange={(e) => setSubject({ ...subject, key: e.target.value })}>
            {['applicationId', 'userId', 'jobId', 'sessionId'].map((k) => (
              <option key={k}>{k}</option>
            ))}
          </Select>
          <Input className="mono w-64" placeholder="equals…" value={subject.value} onChange={(e) => setSubject({ ...subject, value: e.target.value.trim() })} />
        </div>
      </div>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <div className="p-4">
          <ErrorNote error={q.error} />
        </div>
      ) : rows.length === 0 ? (
        <Empty title="No requests match">Requests appear here a few seconds after the product calls an agent.</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-[13px]">
            <thead>
              <tr className="border-b border-line text-left text-[11px] tracking-wide text-ink-3 uppercase">
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">{showAgent ? 'Agent' : 'Version'}</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Subject</th>
                <th className="px-3 py-2 text-right font-medium">Duration</th>
                <th className="px-3 py-2 text-right font-medium">Tokens</th>
                <th className="px-4 py-2 text-right font-medium">Cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr
                  key={t.id}
                  className="cursor-pointer border-b border-line-2 last:border-0 hover:bg-line-2/50"
                  // The whole row opens the request (not only the time); selecting text doesn't.
                  onClick={() => window.getSelection()?.toString() || void navigate({ to: '/requests/$traceId', params: { traceId: t.id } })}
                >
                  <td className="px-4 py-2">
                    <Link to="/requests/$traceId" params={{ traceId: t.id }} className="text-ink hover:underline" title={t.startedAt} onClick={(e) => e.stopPropagation()}>
                      {fmtWhen(t.startedAt)}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    {showAgent && <span className="mono">{t.agentSlug}</span>}
                    <span className={showAgent ? 'ml-1.5 text-ink-3' : 'text-ink-2'}>{t.version ? `v${t.version}` : t.source === 'playground' ? 'draft' : 'fallback'}</span>
                  </td>
                  <td className="max-w-[260px] px-3 py-2">
                    {t.status === 'ok' ? (
                      <Badge tone="ok">ok</Badge>
                    ) : (
                      <span className="flex items-center gap-1.5">
                        <Badge tone="bad">error</Badge>
                        <span className="truncate text-xs text-bad" title={t.error ?? ''}>
                          {t.error}
                        </span>
                      </span>
                    )}
                  </td>
                  <td className="max-w-[280px] px-3 py-2">
                    <span className="mono block truncate text-xs text-ink-2">
                      {Object.entries(t.subject ?? {})
                        .map(([k, v]) => `${k}=${v}`)
                        .join(' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMs(t.durationMs)}</td>
                  <td className="px-3 py-2 text-right text-ink-2 tabular-nums">
                    {fmtInt(t.inputTokens)} / {fmtInt(t.outputTokens)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtUsd(t.costUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {q.hasNextPage && (
            <div className="border-t border-line p-3 text-center">
              <Button size="sm" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                Load more
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
